import React, { createContext, useEffect, useState } from 'react';
import AuthService from '../services/authService';
import { UserClass } from '../user';
import ProfileService, { ProfileClass } from '../profile';

interface ProfileContextInterface {
    user: UserClass;
    isLoadingUser: boolean;
    setUser: (user: UserClass) => void;
    signOut: () => void;
    setUserData: (key: string, value: any) => UserClass;
    getProfileById: (ID: string) => Promise<ProfileClass>;
    getProfilesById: (IDs: string[]) => Promise<ProfileClass[]>;
    getSharedProfiles: (forced: boolean) => Promise<ProfileClass[]>;
}

const ProfileContext = createContext({} as ProfileContextInterface);

const ProfileContextProvider = (props: { children: React.ReactElement }) => {
    const authService = new AuthService();
    const profileService = new ProfileService();

    const [user, setUser] = useState<UserClass>({} as UserClass);
    const [profiles, setProfiles] = useState<ProfileClass[]>([]);
    const [isLoadingUser, setLoadingUser] = useState(true);

    const setUserData = (key: string, value: any): UserClass => {
        user[key] = value;
        setUser(user);
        return user;
    };

    const signOut = (): void => {
        authService.logoutUser().then((userOut) => {
            setUser({} as UserClass);
        });
    };

    const getProfiles = async (forced?: boolean): Promise<ProfileClass[]> => {
        let result: ProfileClass[] = profiles;
        if (!result.length || forced) {
            result = (await profileService.getAll()) as ProfileClass[];
            setProfiles(result);
        }
        return result;
    };

    const getProfileById = async (ID: string): Promise<ProfileClass> => {
        return (await getProfiles()).find(
            (profile) => ID === profile.id
        ) as ProfileClass;
    };

    const getProfilesById = async (IDs: string[]): Promise<ProfileClass[]> => {
        return (await getProfiles()).filter((profile) =>
            IDs.includes(profile.id)
        );
    };

    const getSharedProfiles = async (
        forced: boolean
    ): Promise<ProfileClass[]> => {
        return (await getProfiles(forced)).filter(
            (profile) => profile.public || false
        );
    };

    useEffect(() => {
        authService.getAuthState((user: UserClass) => {
            setUser(user);
            setLoadingUser(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ProfileContext.Provider
            value={{
                user,
                isLoadingUser,
                setUser,
                setUserData,
                signOut,
                getProfileById,
                getProfilesById,
                getSharedProfiles
            }}
        >
            {props.children}
        </ProfileContext.Provider>
    );
};

export default ProfileContext;
export { ProfileContextProvider };
